import { device } from "@/style/constants";
import { pxToRem } from "@/style/utils";
import styled, { createGlobalStyle, css } from "styled-components";

export enum CustomFooterStyles {
  Generic = "Generic",
  Redeem = "Redeem",
  FullFooter = "FullFooter",
  SmallFooter = "SmallFooter",
}

const GenericFooterStyle = css`
  /* Footer styles */
  #riotbar-footer-container {
    display: block;
    margin-top: ${pxToRem(2)};
    padding-bottom: ${pxToRem(80)};
  }
  .riotbar-footer-content {
    &&& {
      display: flex;
      flex-direction: column-reverse;
      padding-bottom: 0;
      position: relative;
      background-color: unset;
      border-top: none;
    }
    .riotbar-footer-logos,
    .riotbar-copyright,
    .riotbar-legal-links {
      margin: ${pxToRem(12)} auto;
      font-size: 0.64rem;
      color: rgb(184, 184, 184);
      font-weight: 800;
      padding: 0;
    }

    .riotbar-legal-links ul {
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
    .riotbar-legal-links ul li {
      margin: 0;
    }
    .riotbar-legal-links ul li a {
      color: ${({ theme }) => theme.colors["gray.99"]};
      font-weight: 800;
      font-size: ${pxToRem(10.24)};
      padding: 4px 8px;
    }
    .riotbar-footer-logos {
      display: none;
    }
    .riotbar-copyright {
      color: rgb(82, 82, 82);
      margin-top: 0;
    }
    .riotbar-copyright > p {
      color: ${({ theme }) => theme.colors["gray.99-alpha.60"]};
      font-weight: 800;
      font-size: ${pxToRem(8.192)};
    }
  }

  @media ${device.laptop} {
    #riotbar-footer-container {
      padding-bottom: ${pxToRem(64)};
      margin-top: ${pxToRem(48)};
    }
  }
`;

const RedeemFrontpageFooter = css`
  ${GenericFooterStyle}
  html[lang=zh-tw], html[lang=vi-vn], html[lang=vn-vn] {
    .riotbar-footer-content {
      .riotbar-footer-logos {
        display: inherit;
        align-items: center;
      }
    }
  }
`;

const FullFooterStyle = css`
  /* Footer styles */
  #riotbar-footer-container {
    display: block;
    margin-top: ${pxToRem(113)}; /* Unable to use theme values in global styles */
    background: ${({ theme }) => theme.colors["gray.12"]};
  }

  .riotbar-footer-content {
    &&& {
      position: relative;
      border-top: none;
      padding: 1px 5px 50px;
      display: flex;
      flex-direction: column;
      background-color: ${({ theme }) => theme.colors["gray.12"]};
    }

    .riotbar-footer-logos,
    .riotbar-copyright,
    .riotbar-legal-links {
      margin: ${pxToRem(12)} auto;
      font-size: 0.64rem;
      color: ${({ theme }) => theme.colors["gray.99"]};
      font-weight: 800;
    }

    .riotbar-copyright {
      color: rgb(82, 82, 82);
      margin-top: 0;
    }

    .riotbar-copyright > p {
      margin: ${pxToRem(48)} auto;
      font-size: ${pxToRem(13)};
      color: ${({ theme }) => theme.colors["gray.68"]};
    }

    &&& .riotbar-legal-links ul li a {
      color: ${({ theme }) => theme.colors["gray.99"]};
      letter-spacing: 0.012rem;
      font-size: ${pxToRem(13)};
      padding: 1rem 2rem;
    }
  }
`;

const SmallFooterStyle = css`
  /* Footer styles */
  #riotbar-footer-container {
    display: block;
    padding-top: 80px;
  }

  .riotbar-footer-content {
    &&& {
      position: relative;
      background-color: unset;
      border-top: none;
      padding: 1px 5px 16px;
      display: flex;
      flex-direction: column;
    }

    .riotbar-footer-links {
      background: unset;
    }

    .riotbar-footer-links ul li {
      display: none;
      color: ${({ theme }) => theme.colors["gray.99"]};
      :nth-child(4) {
        display: unset;
        font-size: ${pxToRem(13)};
      }
    }

    .riotbar-copyright > p {
      margin: ${pxToRem(48)} auto;
      font-size: ${pxToRem(13)};
      color: ${({ theme }) => theme.colors["gray.68"]};
    }
    .riotbar-footer-logos,
    .riotbar-social-links {
      display: none;
    }
    .riotbar-copyright {
      color: rgb(82, 82, 82);
      margin-top: 0;
    }
    &&& .riotbar-legal-links ul li a {
      color: ${({ theme }) => theme.colors["gray.99"]};
      letter-spacing: 0.012rem;
      font-size: ${pxToRem(13)};
      padding: 8px 16px;
    }
    .riotbar-game-rating {
      padding: 1rem;
    }
    .riotbar-game-ratings {
      margin-bottom: ${pxToRem(25)};
      align-items: stretch;
      gap: 1rem;
      .riotbar-game-rating-text {
        display: none;
      }
      .riotbar-game-rating {
        display: flex;
      }
    }
  }
`;

const handleCustomFooter = (customFooterStyles?: CustomFooterStyles) => {
  if (!customFooterStyles) {
    return GenericFooterStyle;
  }
  const footerStyle = {
    [CustomFooterStyles.Generic]: GenericFooterStyle,
    [CustomFooterStyles.Redeem]: RedeemFrontpageFooter,
    [CustomFooterStyles.FullFooter]: FullFooterStyle,
    [CustomFooterStyles.SmallFooter]: SmallFooterStyle,
  };

  return footerStyle[customFooterStyles];
};

export const RemoveRiotbarLinks = createGlobalStyle`
.riotbar-root {
  .riotbar-desktop-navigation-wrapper {
    visibility: hidden;
  }
}
`;

export const RiotBarContainer = createGlobalStyle<{ footerStyle?: CustomFooterStyles }>`
  /*
    Riotbar style changes.
  */
  body {
    overscroll-behavior-y: none;
    margin-top: 80px !important;
  }

  .locale-list {
    .active-language {
      position: relative;
      span {
        color: #c4202b !important;
      }
      svg {
        display: inline-block;
        position: absolute;
        top: 12px;
        right: 14px;
        g {
          display: block;
        }
      }
    }
  }

  #riotbar-bar {
    transition: background-color 0.2s ease;

    svg {
      /*
        this needs to be set manually since our normalize.css sets this to block.
        context: https://riotgames.slack.com/archives/C2RH6HS3D/p1607360149165300
      */
      display: inline-block;
    }
  }
  ${({ footerStyle }) => handleCustomFooter(footerStyle)}
`;

/**
 * Small placeholder component to draw a dark box under where the riotbar would render.
 */
export const RiotbarHolder = styled.div`
  display: block;
  position: fixed;

  z-index: 1;
  background-color: rgb(17, 17, 17);

  top: 0;
  left: 50%;
  transform: translateX(-50%);

  height: 80px;
  width: 100%;
  max-width: 1920px;
`;
